import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ShieldCheck, Star, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("Bon retour !"); nav("/dashboard"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur de connexion"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left panel — story */}
      <div className="hidden md:flex md:w-1/2 relative items-center justify-center p-12 overflow-hidden border-r border-border">
        <div className="aurora opacity-80" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-md fade-up">
          <Link to="/"><Logo size={26} /></Link>
          <h2 className="mt-12 text-4xl lg:text-5xl font-black tracking-tighter font-display leading-tight">
            La plateforme<br />
            <span className="text-gradient">de croissance</span><br />
            <span className="font-serif-italic font-normal">des commerces locaux.</span>
          </h2>
          <p className="text-muted-foreground mt-6 text-base leading-relaxed">
            Visibilité, contenus et réputation — réunis dans un seul tableau de bord premium.
          </p>

          <div className="mt-10 space-y-3">
            {[
              "Diagnostic en moins de 60 secondes",
              "Suivi des concurrents en temps réel",
              "Studio de contenu intégré",
            ].map(t => (
              <div key={t} className="flex items-center gap-2 text-sm">
                <span className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center">
                  <ShieldCheck className="h-3 w-3 text-primary" />
                </span>
                {t}
              </div>
            ))}
          </div>

          <div className="mt-12 p-5 rounded-2xl glass border border-border">
            <div className="flex gap-0.5 text-amber-500 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-500" />)}</div>
            <p className="text-sm leading-relaxed">"Notre fiche est passée 1ère sur « bistrot Châtelet ». Le brunch affiche complet trois semaines à l'avance."</p>
            <p className="text-xs text-muted-foreground mt-3">Sophie L. · Le Petit Bistrot · Paris 1er</p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="login-form">
          <div className="md:hidden mb-6"><Link to="/"><Logo size={22} /></Link></div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter font-display">Connexion</h1>
            <p className="text-sm text-muted-foreground mt-1.5">Heureux de vous revoir.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider font-bold">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-md h-11" placeholder="vous@exemple.fr" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="pwd" className="text-xs uppercase tracking-wider font-bold">Mot de passe</Label>
              <Link to="/reset" className="text-xs text-primary hover:underline" data-testid="login-reset-link">Oublié ?</Link>
            </div>
            <Input id="pwd" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="rounded-md h-11" placeholder="••••••••" data-testid="login-password-input" />
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-glow" data-testid="login-submit-btn">
            {loading ? "Connexion…" : "Se connecter"}<ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <p className="text-sm text-center text-muted-foreground">
            Pas encore de compte ? <Link to="/signup" className="text-primary hover:underline font-medium" data-testid="login-signup-link">Créer un compte</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
